import { DevTool } from "@hookform/devtools";
import { useEffect, useId, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { fetcher } from "../../apiHelpers";
import { API_ADMIN_ROUTES, ERROR_MESSAGE } from "../../constants";
import useUserStore from "../../store/User.store";

import AdminUserAddCard from "../../components/AdminAddUserCard";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { showToast } from "../../components/Toast";
import Toolbar from "../../components/Toolbar";

import "./admin.scss";

const AdminAddUser = () => {
  const defaultValues = useMemo(
    () => ({
      usersData: [{ fname: "", lname: "", email: "" }],
    }),
    []
  );
  const navigate = useNavigate();

  const [isAbleToSubmit, setIsAbleToSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFormFailModal, setShowFormFailModal] = useState(false);

  const methods = useForm({
    mode: "onTouched",
    defaultValues,
  });

  const removeEmptyUsersFromArray = (users = []) => {
    return users.filter((val) => Object.values(val).every((v) => v !== ""));
  };

  const handleFormError = (errors, e) => console.log(errors, e);

  const { user } = useUserStore().data;
  const token = user?.accessToken;

  const handlePostAdminUsers = async (data) => {
    return fetcher({
      url: API_ADMIN_ROUTES.ADMIN_LIST,
      token,
      method: "POST",
      data,
    });
  };

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    const users = removeEmptyUsersFromArray(usersData);
    const response = await handlePostAdminUsers({ users });
    try {
      const errorUsers = response.filter((user) => !user?.created);
      const numberOfCreatedUsers = response.filter(
        (user) => user?.created
      ).length;
      if (errorUsers.length > 0) {
        setShowFormFailModal(true);
      } else {
        showToast(() => (
          <span>{`${numberOfCreatedUsers} User(s) have been successfully created.`}</span>
        ));
        navigate(`/admin/users`, { replace: true });
      }
    } catch (error) {
      console.warn(error);
      setShowFormFailModal(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentErrors.length === 0) {
      methods.handleSubmit(handleFormSubmit, handleFormError)(e);
    }
  };

  const usersData = useWatch({
    control: methods.control,
    name: "usersData",
  });

  const { errors } = methods.formState;
  const currentErrors = Object.keys(errors);

  useEffect(() => {
    if (!usersData) {
      methods.reset(defaultValues);
    }

    setIsAbleToSubmit(
      currentErrors.length === 0 &&
        removeEmptyUsersFromArray(usersData).length > 0
    );
  }, [usersData, currentErrors, methods, defaultValues]);

  const headerCTA = (
    <>
      <Button
        size="small"
        className="toolbarHeaderCta"
        onClick={handleSubmit}
        disabled={!isAbleToSubmit || isSubmitting}
      >
        Submit
      </Button>
    </>
  );

  const id = useId();

  return (
    <>
      <FormProvider {...methods}>
        <form className="admin-user-form" onSubmit={handleSubmit}>
          <AdminUserAddCard id={id} />
          <Toolbar
            title="Quick Actions"
            data={null}
            headerCTA={headerCTA}
            collapsible={false}
          />
        </form>
      </FormProvider>
      <Modal
        variation="alert"
        show={showFormFailModal}
        onClose={() => {
          setShowFormFailModal(false);
          setIsSubmitting(false);
        }}
        heading="Form Error!"
        subtitle={ERROR_MESSAGE.SUBTITLE.TRY_AGAIN}
        cancelButton={{
          variant: "outline",
          children: "Close",
        }}
        confirmButton={null}
        iconName="system-warning-filled"
        iconTheme="error"
      />

      {process.env.REACT_APP_ENV === "local" && (
        <DevTool control={methods.control} />
      )}
    </>
  );
};

export default AdminAddUser;
